import { Type, type Static } from "@sinclair/typebox";
import { ExternalAccountBindingSchema } from "./external-account.ts";
import { UuidSchema } from "./identifiers.ts";
import { GlobalProductRoleSchema } from "./product-authorization.ts";

const TimestampSchema = Type.String({ format: "date-time" });
const NullableTimestampSchema = Type.Union([TimestampSchema, Type.Null()]);

export const ProductAccountStatusSchema = Type.Union([
  Type.Literal("reserved"),
  Type.Literal("invited"),
  Type.Literal("active"),
  Type.Literal("disabled")
]);
export type ProductAccountStatus = Static<typeof ProductAccountStatusSchema>;

export const ProductAccountSchema = Type.Object(
  {
    principalId: UuidSchema,
    email: Type.String({ format: "email", maxLength: 320 }),
    displayName: Type.Union([Type.String({ minLength: 1, maxLength: 256 }), Type.Null()]),
    status: ProductAccountStatusSchema,
    createdAt: TimestampSchema,
    activationSentAt: NullableTimestampSchema,
    confirmedAt: NullableTimestampSchema,
    lastSignInAt: NullableTimestampSchema,
    disabledUntil: NullableTimestampSchema,
    globalRole: Type.Union([GlobalProductRoleSchema, Type.Null()]),
    externalBindings: Type.Array(ExternalAccountBindingSchema, { maxItems: 2 })
  },
  { additionalProperties: false }
);
export type ProductAccount = Static<typeof ProductAccountSchema>;

export const ProductAccountPageSchema = Type.Object(
  {
    accounts: Type.Array(ProductAccountSchema, { maxItems: 200 }),
    nextPage: Type.Union([Type.Integer({ minimum: 2 }), Type.Null()])
  },
  { additionalProperties: false }
);
export type ProductAccountPage = Static<typeof ProductAccountPageSchema>;

export const ProductAccountReferenceSchema = Type.Object(
  {
    principalId: UuidSchema,
    email: Type.String({ format: "email", maxLength: 320 }),
    displayName: Type.Union([Type.String({ minLength: 1, maxLength: 256 }), Type.Null()]),
    status: ProductAccountStatusSchema
  },
  { additionalProperties: false }
);
export type ProductAccountReference = Static<typeof ProductAccountReferenceSchema>;

export const ProductAccountReferencePageSchema = Type.Object(
  {
    accounts: Type.Array(ProductAccountReferenceSchema, { maxItems: 50 }),
    truncated: Type.Boolean()
  },
  { additionalProperties: false }
);
export type ProductAccountReferencePage = Static<typeof ProductAccountReferencePageSchema>;

export const ProductAccountCreateInputSchema = Type.Object(
  {
    email: Type.String({ format: "email", maxLength: 320 }),
    displayName: Type.Optional(Type.String({ minLength: 1, maxLength: 256 })),
    idempotencyKey: Type.String({ minLength: 1, maxLength: 128 })
  },
  { additionalProperties: false }
);
export type ProductAccountCreateInput = Static<typeof ProductAccountCreateInputSchema>;

export const ProductAccountSelfRegistrationInputSchema = Type.Object(
  {
    email: Type.String({ format: "email", maxLength: 320 }),
    displayName: Type.String({ minLength: 1, maxLength: 256, pattern: ".*\\S.*" }),
    password: Type.String({ minLength: 12, maxLength: 128 })
  },
  { additionalProperties: false }
);
export type ProductAccountSelfRegistrationInput = Static<
  typeof ProductAccountSelfRegistrationInputSchema
>;

export const ProductAccountSelfRegistrationResultSchema = Type.Object(
  {
    registered: Type.Literal(true),
    downloadUrl: Type.Literal("/v1/desktop-releases/latest")
  },
  { additionalProperties: false }
);
export type ProductAccountSelfRegistrationResult = Static<
  typeof ProductAccountSelfRegistrationResultSchema
>;

export const ProductAccountCommandResultSchema = Type.Object(
  { account: ProductAccountSchema },
  { additionalProperties: false }
);
export type ProductAccountCommandResult = Static<typeof ProductAccountCommandResultSchema>;

export const ProductAccountErrorCodeSchema = Type.Union([
  Type.Literal("PRODUCT_ACCOUNT_NOT_FOUND"),
  Type.Literal("ACCOUNT_IDEMPOTENCY_CONFLICT"),
  Type.Literal("ACCOUNT_EMAIL_CONFLICT"),
  Type.Literal("ACCOUNT_ACTIVATION_NOT_READY"),
  Type.Literal("ACCOUNT_ACTIVATION_NOT_RELEASED"),
  Type.Literal("ACCOUNT_ACTIVATION_NOT_APPLICABLE"),
  Type.Literal("ACCOUNT_ACTIVATION_RELEASE_FAILED"),
  Type.Literal("EXTERNAL_ACCOUNT_PROVISION_FAILED"),
  Type.Literal("EXTERNAL_ACCOUNT_SUSPEND_FAILED"),
  Type.Literal("EXTERNAL_ACCOUNT_RESTORE_FAILED"),
  Type.Literal("EXTERNAL_ACCOUNT_IDENTITY_COLLISION"),
  Type.Literal("EXTERNAL_ACCOUNT_BINDING_CONFLICT"),
  Type.Literal("EXTERNAL_ACCOUNT_BINDING_REQUIRED"),
  Type.Literal("EXTERNAL_ACCOUNT_BINDING_INVALID"),
  Type.Literal("EXTERNAL_ACCOUNT_SYSTEM_UNSUPPORTED"),
  Type.Literal("ACCOUNT_SELF_DISABLE_FORBIDDEN"),
  Type.Literal("ROLE_TARGET_MISMATCH"),
  Type.Literal("LAST_SUPER_ADMINISTRATOR_REQUIRED")
]);
export type ProductAccountErrorCode = Static<typeof ProductAccountErrorCodeSchema>;
